import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import SuggestionEditor from "@/components/suggestion-editor";

interface SubmitSuggestionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const suggestionFormSchema = z.object({
  content: z.any(),
  plainContent: z.string().min(10, "Suggestion must be at least 10 characters"),
  screenshots: z.array(z.object({
    name: z.string(),
    data: z.string(),
  })).optional(),
  userAlias: z.string().max(50, "Alias must be 50 characters or less").optional(),
});

type SuggestionFormData = z.infer<typeof suggestionFormSchema>;

export default function SubmitSuggestionDialog({ open, onOpenChange }: SubmitSuggestionDialogProps) {
  const queryClient = useQueryClient();
  const [screenshots, setScreenshots] = useState<Array<{ name: string; data: string }>>([]);

  const form = useForm<SuggestionFormData>({
    resolver: zodResolver(suggestionFormSchema),
    defaultValues: {
      content: { type: 'doc', content: [{ type: 'paragraph' }] },
      plainContent: "",
      screenshots: [],
      userAlias: "",
    },
  });

  const submitSuggestionMutation = useMutation({
    mutationFn: async (data: SuggestionFormData) => {
      const payload = {
        content: data.content,
        plainContent: data.plainContent,
        screenshots: data.screenshots || [],
        userAlias: data.userAlias || undefined,
      };
      return apiRequest("POST", "/api/suggestions", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/suggestions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/suggestions"] });
      
      form.reset();
      setScreenshots([]);
      onOpenChange(false);
    },
    onError: () => {
    },
  });

  const onSubmit = (data: SuggestionFormData) => {
    submitSuggestionMutation.mutate(data);
  };

  const handleEditorUpdate = ({ content, plainContent }: { content: any; plainContent: string }) => {
    form.setValue('content', content);
    form.setValue('plainContent', plainContent);
  };

  const handleScreenshotsChange = (newScreenshots: Array<{ name: string; data: string }>) => {
    setScreenshots(newScreenshots);
    form.setValue('screenshots', newScreenshots);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-submit-suggestion">
        <DialogHeader>
          <DialogTitle>Submit a Suggestion</DialogTitle>
          <DialogDescription>
            Have an idea for a new feature or improvement? We'd love to hear from you!
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormItem>
              <FormLabel>Your Suggestion or Error Report</FormLabel>
              <FormControl>
                <SuggestionEditor
                  initialContent={form.getValues('content')}
                  onUpdate={handleEditorUpdate}
                  placeholder="Describe your feature request, improvement idea, or report an error. You can format text and attach screenshots!"
                  screenshots={screenshots}
                  onScreenshotsChange={handleScreenshotsChange}
                />
              </FormControl>
              {form.formState.errors.plainContent && (
                <p className="text-sm font-medium text-destructive">
                  {form.formState.errors.plainContent.message}
                </p>
              )}
            </FormItem>

            <FormField
              control={form.control}
              name="userAlias"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display Name (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="How would you like to be displayed publicly?"
                      data-testid="input-suggestion-alias"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-xs text-muted-foreground">
                    Leave blank to use your real name
                  </p>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="button-cancel-suggestion"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitSuggestionMutation.isPending}
                data-testid="button-submit-suggestion"
              >
                {submitSuggestionMutation.isPending ? "Submitting..." : "Submit Suggestion"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
